<template>
  <div class="friend-detail">
    <!-- 这一行就是右侧面板的标题：头像 + 好友名字 + 分享了 N 张工作表 -->
    <div class="detail-head">
      <UserAvatar
        class="friend-avatar"
        :url="friend.avatarUrl"
        :name="displayName"
        :onair="isOnair"
        :title="avatarTitle"
      />
      <div class="info-text">
        <div class="friend-name" :title="displayName">{{ displayName }}</div>
        <div class="friend-sub">{{ projectsText }}</div>
      </div>
      <div v-if="projects.length" class="project-switch">
        <select v-model="selectedProjectId" class="project-select" title="切换工作表">
          <option v-for="p in projects" :key="p.project.id" :value="p.project.id">
            {{ p.project.name }}
          </option>
        </select>
        <button class="comment-project-btn" type="button" title="对整张工作表发表评论" @click="openProjectComment">
          <FaIcon :icon="faCommentDots" :size="13" />
          <span>评论</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-tip">加载中…</div>
    <div v-else-if="!projects.length" class="state-tip">
      这位好友还没有分享任何内容
    </div>
    <FriendTablePanel
      v-else-if="selectedProject"
      :friend-id="friend.friendId"
      :workspace-id="selectedProject.project.workspaceId"
      :project-id="selectedProject.project.id"
      :project-color="selectedProject.project.color || ''"
      :tags="tags"
      :editable="editable"
      :category-visible="categoryVisible"
      @tags-changed="loadTags"
      @activity-changed="onActivityChanged"
    />
    <p v-if="selectedProject" class="hover-hint">
      <FaIcon :icon="faCircleInfo" :size="11" />
      <span>鼠标在时间块上停留半秒，评论气泡会贴在块的右侧出现；点击时间块可固定气泡来写评论</span>
    </p>

    <CommentDialog
      :visible="commentVisible"
      :resource-type="commentResourceType"
      :resource-id="commentResourceId"
      :title="commentTitle"
      @close="commentVisible = false"
    />
  </div>
</template>

<script>
import FriendTablePanel from './FriendTablePanel.vue'
import CommentDialog from './CommentDialog.vue'
import FaIcon from '@/components/common/FaIcon.vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { faCommentDots } from '@fortawesome/free-solid-svg-icons/faCommentDots'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons/faCircleInfo'
import { listFriendProjects, getFriendProjectAccess, listFriendTags } from '@/api/share.js'

export default {
  name: 'FriendDetailPanel',
  components: { FriendTablePanel, CommentDialog, FaIcon, UserAvatar },
  props: {
    friend: {
      type: Object,
      required: true
    },
    /** 该好友的当前动态（{ status, content, projectName, ... }），由好友列表传入，用于头像在线态 */
    activity: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      projects: [],
      loading: false,
      selectedProjectId: null,
      commentVisible: false,
      commentResourceType: 'PROJECT',
      commentResourceId: null,
      commentTitle: '',
      faCommentDots,
      faCircleInfo,
      // 当前所选工作表的分类（标签）列表，取自好友，供时间块渲染分类图标 / 名称 / 配色
      tags: [],
      // 当前登录用户对所选工作表是否拥有「编辑」授权
      editable: false,
      // 好友是否开放了该表的「分类（周累计）」统计表查看权限
      categoryVisible: false
    }
  },
  computed: {
    displayName() {
      return this.friend.remark || this.friend.nickname || '好友'
    },
    /** 好友此刻是否「正在做」：头像外圈绿光环与列表行保持同一判断 */
    isOnair() {
      return !!(this.activity && this.activity.status === 'RUNNING')
    },
    /** 头像悬浮说明：正在做时带上具体内容 */
    avatarTitle() {
      if (!this.isOnair) return ''
      const content = (this.activity.content || '').trim() || '未命名记录'
      return `正在做：${content}`
    },
    selectedProject() {
      return this.projects.find(p => String(p.project.id) === String(this.selectedProjectId)) || null
    },
    projectsText() {
      if (this.loading) return '加载中…'
      if (!this.projects.length) return '暂未分享内容'
      return `分享了 ${this.projects.length} 张工作表`
    }
  },
  watch: {
    friend: {
      immediate: true,
      handler() {
        this.selectedProjectId = null
        this.editable = false
        this.categoryVisible = false
        this.tags = []
        this.loadProjects()
      }
    },
    selectedProjectId() {
      this.fetchAccess()
      this.loadTags()
    }
  },
  methods: {
    async loadProjects() {
      if (!this.friend || !this.friend.friendId) return
      this.loading = true
      try {
        const res = await listFriendProjects(this.friend.friendId)
        if (!res.success || res.code !== 200) {
          this.projects = []
          return
        }
        this.projects = res.data || []
        if (this.projects.length) {
          this.selectedProjectId = this.projects[0].project.id
        }
      } catch (e) {
        console.error('[FriendDetailPanel] 加载好友工作表失败', e)
        this.projects = []
      } finally {
        this.loading = false
      }
    },
    /** 工作表级别的评论仍走弹窗；时间块级别的评论由 FriendTablePanel 显示居中面板。 */
    openProjectComment() {
      if (!this.selectedProject) return
      this.commentResourceType = 'PROJECT'
      this.commentResourceId = this.selectedProject.project.id
      this.commentTitle = this.selectedProject.project.name
      this.commentVisible = true
    },
    /**
     * 查询当前登录用户对所选好友工作表的访问权限，决定好友时间表是否可编辑、
     * 以及是否展示「分类（周累计）」统计表。
     * 仅 EDIT 才放行协同编辑；READ / null 均为只读。
     * 分类统计表由 categoryVisible 单独控制，与只读 / 编辑无关。
     */
    async fetchAccess() {
      if (!this.friend || !this.friend.friendId || !this.selectedProjectId) {
        this.editable = false
        this.categoryVisible = false
        return
      }
      this.editable = false
      this.categoryVisible = false
      try {
        const res = await getFriendProjectAccess(this.friend.friendId, this.selectedProjectId)
        if (!res.success || res.code !== 200) return
        const data = res.data || {}
        this.editable = data.permission === 'EDIT'
        this.categoryVisible = !!data.categoryVisible
      } catch (e) {
        console.warn('[FriendDetailPanel] 查询访问权限失败', e)
        this.editable = false
        this.categoryVisible = false
      }
    },
    /**
     * 好友工作表刷新时，响应里已附带该好友最新实时状态（方案3）。
     * 向上抛给 FriendsPage，用于刷新左侧动态行，省去单独调用动态接口。
     */
    onActivityChanged(payload) {
      this.$emit('activity-changed', payload)
    },
    /**
     * 拉取所选好友工作表的分类列表，交给 FriendTablePanel 渲染时间块。
     * 拿到后时间块就能显示「分类图标 + 名称」并按分类色板上色，与自己的时间表一致。
     * 失败时降级为空数组（时间块退化为中性底色），不影响时间表本身展示。
     */
    async loadTags() {
      if (!this.friend || !this.friend.friendId || !this.selectedProjectId) {
        this.tags = []
        return
      }
      this.tags = []
      try {
        const res = await listFriendTags(this.friend.friendId, this.selectedProjectId)
        if (!res.success || res.code !== 200) return
        this.tags = res.data || []
      } catch (e) {
        console.warn('[FriendDetailPanel] 加载好友工作表分类失败', e)
        this.tags = []
      }
    }
  }
}
</script>

<style scoped>
.friend-detail {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  gap: 12px;
}

/* 这一行充当右侧面板的标题，字号/间距对齐 FriendsPage 里的 .title */
.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.friend-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.info-text {
  flex: 1;
  min-width: 0;
}

.friend-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #b8d49b;
  border: 2px solid #57795c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.friend-name {
  font-size: 22px;
  font-weight: bold;
  line-height: 1.2;
  color: #263b29;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.friend-sub {
  font-size: 12px;
  color: #57795c;
  margin-top: 3px;
}

.project-switch {
  display: flex;
  align-items: center;
  gap: 8px;
}

.project-select {
  border: 2px solid #91a67b;
  background: #f7f7df;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 13px;
  color: #263b29;
  outline: none;
  max-width: 200px;
}

.comment-project-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 2px solid #987a2c;
  background: #f1dc7b;
  color: #263b29;
  font-weight: 700;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
}

.comment-project-btn:hover {
  background: #e8d06a;
}

.hover-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  flex-shrink: 0;
  font-size: 11px;
  color: #8a9a78;
  margin: -4px 0 0;
}

.state-tip {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8a9a78;
  font-size: 14px;
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸与间距全部沿用上面的原值。 */
/* 头像 / 标题 */
.friend-avatar{
  background:var(--lp-primary,#4aa46a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
.friend-name{
  color:var(--lp-text,#26332b);
}
.friend-sub{
  color:var(--lp-text-2,#5f7268);
}
/* 工作表下拉 + 评论按钮 */
.project-select{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  color:var(--lp-text,#26332b);
}
.comment-project-btn{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary,#4aa46a);
  border-radius:0.5rem;
  color:#fff;
}
.comment-project-btn:hover{
  background:var(--lp-primary-dark,#2f7a4a);
}
/* 提示 / 空态 */
.hover-hint{
  color:var(--lp-text-3,#94a59b);
}
.state-tip{
  color:var(--lp-text-3,#94a59b);
}
</style>
